import React from 'react';
import {
  Typography,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Rocket, CheckCircle2, Sparkles, Clock, Target } from 'lucide-mui';
import ActionDialog from '../../globalModals/ActionDialog';
import type { ActionType } from '../../globalModals/ActionDialog';

interface AICreditLearnMoreProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  feature: 'askAI' | 'templates';
}

const featureDescriptions = {
  askAI: {
    title: 'About AI Chat',
    description:
      'Ask questions about your content and get instant answers, summaries, and insights through simple conversation.',
    benefits: [
      {
        title: 'Quick Navigation',
        description: 'Jump to any moment by asking about it',
        icon: Clock,
      },
      {
        title: 'Smart Insights',
        description: 'Get key points and summaries instantly',
        icon: Target,
      },
      {
        title: 'Save Time',
        description: 'Find what you need without watching the whole recording',
        icon: Sparkles,
      },
    ],
  },
  templates: {
    title: 'About Templates',
    description:
      'Turn your recordings into organized notes with ready-to-use templates that capture exactly what you need.',
    benefits: [
      {
        title: 'Quick Summaries',
        description: 'Get action items and key points automatically',
        icon: Target,
      },
      {
        title: 'Work Faster',
        description: 'Process hours of content in minutes',
        icon: Clock,
      },
      {
        title: 'Pick Your Style',
        description: 'Choose templates for meetings, interviews, and more',
        icon: Sparkles,
      },
    ],
  },
};

const AICreditLearnMore: React.FC<AICreditLearnMoreProps> = ({
  open,
  onClose,
  onUpgrade,
  feature,
}) => {
  const { title, description, benefits } = featureDescriptions[feature];

  const actions: ActionType[] = [
    {
      key: 'close',
      label: 'Close',
      onClick: onClose,
      variant: 'outlined',
      color: 'secondary',
    },
    {
      key: 'upgrade',
      label: 'Upgrade Now',
      onClick: onUpgrade,
      variant: 'contained',
      color: 'primary',
      startIcon: <Rocket style={{ width: '18px', height: '16px' }} />,
    },
  ];

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title={title}
      actions={actions}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        <Typography color="text.secondary">{description}</Typography>

        <List sx={{ py: 0 }}>
          {benefits.map((benefit, index) => (
            <ListItem key={index} sx={{ px: 0, py: 1 }}>
              <ListItemIcon>
                <benefit.icon
                  style={{
                    color: 'var(--mui-palette-primary-main)',
                    width: '20px',
                    height: '20px',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={benefit.title}
                secondary={benefit.description}
                primaryTypographyProps={{
                  fontWeight: 500,
                  sx: { mb: 0.5 },
                }}
              />
            </ListItem>
          ))}
        </List>

        <Stack spacing={1.5}>
          <Typography color="text.secondary">
            <strong>About AI Credits:</strong> Use your credits on any recording
            to chat, summarize, or analyze. Each AI feature uses one credit from
            your plan.
          </Typography>

          <Typography
            variant="body2"
            color="primary"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <CheckCircle2
              style={{
                color: 'var(--mui-palette-primary-main)',
                width: '16px',
                height: '16px',
              }}
            />
            Upgrade for unlimited AI features
          </Typography>
        </Stack>
      </Stack>
    </ActionDialog>
  );
};

export default AICreditLearnMore;
